import { useLocation, useParams } from "react-router";
import { useVbsWorkOrderShowQuery } from "@/services/backend/vbs/work-orders/api/work-order-api";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/services/backend/vbs/work-orders/components/work-order-pdf";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { Separator } from "@/shared/components/ui/separator";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/vbs/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { SignatureButton } from "@/services/backend/vbs/work-orders/components/signature-button";
import { BackToPlanningDialog } from "@/services/backend/vbs/work-orders/components/back-to-planning-dialog";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { CompleteExecutionDialog } from "@/services/backend/vbs/work-orders/components/complete-execution-dialog";
import { AddExtraWorkDuringExecutionDropDown } from "@/routes/processes/[processId]/vbs/work-orders/execution/[workOrderId]/treatment/_components/add-extra-work-during-execution-drop-down";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/vbs/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderExecutionRoute() {
  const { workOrderId } = useParams();
  const {
    data: workOrder,
    isLoading,
    error,
  } = useVbsWorkOrderShowQuery({
    id: workOrderId!,
  });

  useWorkOrderStateGuard(workOrder, FulfillmentState.Execution);

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="execution">
      {t("Ausführung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && (
        <PersistenceContextProvider>
          <Page workOrder={workOrder} />
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const links = [
    {
      to: "base-data",
      label: t("Daten"),
    },
    {
      to: `last-minute-risk-analysis/${workOrder.lastMinuteRiskAnalysisId}`,
      label: t("LMRA"),
    },
    {
      to: "treatment",
      label: t("Behandlung"),
    },
    {
      to: `documentation/${workOrder.documentationId}`,
      label: t("Dokumentation"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  const location = useLocation();

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink key={link.to} to={link.to}>
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          {location.pathname.endsWith("treatment") && (
            <>
              <Separator orientation="vertical" className="min-h-8" />
              <AddExtraWorkDuringExecutionDropDown workOrder={workOrder} />
            </>
          )}
          <Separator orientation="vertical" className="min-h-8" />
          <SignatureButton workOrderID={workOrder.id} />
          <WorkOrderPdf workOrderId={workOrder.id} />
          <Separator orientation="vertical" className="min-h-8" />
          <BackToPlanningDialog workOrder={workOrder} />
          <CompleteExecutionDialog workOrder={workOrder} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
